import { useEffect, useLayoutEffect, useRef, useState, type ComponentType } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { telaLiberadaNaEmergencia, useEmergencia } from '../lib/emergencia';

interface IconProps { size?: number }
type IconCmp = ComponentType<IconProps>;

export interface MenuItem {
  to?: string;
  onClick?: () => void;
  label: string;
  icon: IconCmp;
  children?: MenuItem[];
}

interface DropdownMenuProps {
  label: string;
  icon: IconCmp;
  items: MenuItem[];
  iconSize?: number;
  iconOnly?: boolean;
  alignRight?: boolean;
  title?: string;
}

interface PosicaoFixa { top: number; left?: number; right?: number; abrirCima: boolean }

function calcularPos(anchor: DOMRect, alignRight: boolean, alturaEstimada = 320): PosicaoFixa {
  const margin = 4;
  const espacoBaixo = window.innerHeight - anchor.bottom;
  const abrirCima = espacoBaixo < alturaEstimada && anchor.top > espacoBaixo;
  const top = abrirCima ? anchor.top - margin : anchor.bottom + margin;
  if (alignRight) {
    return { top, right: window.innerWidth - anchor.right, abrirCima };
  }
  return { top, left: anchor.left, abrirCima };
}

export function DropdownMenu({ label, icon: Icon, items, iconSize = 18, iconOnly = false, alignRight = false, title }: DropdownMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [subAberto, setSubAberto] = useState<string | null>(null);
  const [pos, setPos] = useState<PosicaoFixa | null>(null);
  const [posSub, setPosSub] = useState<PosicaoFixa | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);
  const subItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function fora(e: MouseEvent) {
      const target = e.target as Node;
      if (painelRef.current?.contains(target)) return;
      if (btnRef.current?.contains(target)) return;
      setOpen(false);
      setSubAberto(null);
    }
    if (open) document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    setPos(calcularPos(btnRef.current.getBoundingClientRect(), alignRight));
  }, [open, alignRight]);

  useLayoutEffect(() => {
    if (!subAberto || !subItemRef.current) {
      setPosSub(null);
      return;
    }
    const r = subItemRef.current.getBoundingClientRect();
    const espacoDireita = window.innerWidth - r.right;
    const margin = 4;
    setPosSub({
      top: r.top,
      left: espacoDireita < 220 ? r.left - 220 - margin : r.right + margin,
      abrirCima: false,
    });
  }, [subAberto]);

  const url = location.pathname + location.search;
  const isAtivo = (to: string | undefined) => {
    if (!to) return false;
    if (to.includes('?')) return url === to;
    return location.pathname.startsWith(to);
  };
  const grupoAtivo = items.some((i) => i.children ? i.children.some((c) => isAtivo(c.to)) : isAtivo(i.to));

  // MODO EMERGENCIA: so as telas de TELAS_EMERGENCIA respondem; o resto aparece apagado.
  const emergencia = useEmergencia();
  const bloqueado = (item: MenuItem): boolean => {
    if (!emergencia) return false;
    if (item.children) return item.children.every(bloqueado);
    return !item.to || !telaLiberadaNaEmergencia(item.to);
  };
  const estiloBloqueado = { opacity: 0.4, cursor: 'not-allowed' } as const;
  const DICA_BLOQUEADO = 'Indisponivel no modo emergencia';

  function navegar(to: string) {
    navigate(to);
    setOpen(false);
    setSubAberto(null);
  }

  function executarItem(item: MenuItem) {
    if (item.onClick) {
      item.onClick();
      setOpen(false);
      setSubAberto(null);
    } else if (item.to) {
      navegar(item.to);
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => { setOpen(!open); setSubAberto(null); }}
        className={iconOnly ? `btn-icon-header ${grupoAtivo ? 'menu-btn-ativo' : ''}` : `menu-btn ${grupoAtivo ? 'menu-btn-ativo' : ''}`}
        title={title ?? (iconOnly ? label : undefined)}
        aria-label={iconOnly ? label : undefined}
      >
        <Icon size={iconSize} />
        {!iconOnly && (
          <>
            <span>{label}</span>
            <ChevronDown size={14} className={`chev ${open ? 'chev-aberto' : ''}`} />
          </>
        )}
      </button>
      {open && pos && (
        <div
          ref={painelRef}
          className="dropdown-painel"
          style={{
            position: 'fixed',
            top: pos.abrirCima ? undefined : pos.top,
            bottom: pos.abrirCima ? window.innerHeight - pos.top : undefined,
            left: alignRight ? 'auto' : pos.left,
            right: pos.right,
            margin: 0,
            minWidth: alignRight ? 180 : undefined,
          }}
        >
          {items.map((item) => item.children ? (
            <div
              key={item.label}
              ref={subAberto === item.label ? subItemRef : null}
              className="dropdown-sub"
            >
              <button
                type="button"
                onClick={() => setSubAberto(subAberto === item.label ? null : item.label)}
                disabled={bloqueado(item)}
                title={bloqueado(item) ? DICA_BLOQUEADO : undefined}
                style={bloqueado(item) ? estiloBloqueado : undefined}
                className={`dropdown-item ${item.children.some((c) => isAtivo(c.to)) ? 'dropdown-item-ativo' : ''}`}
              >
                <span className="dropdown-item-label">
                  <item.icon size={16} />
                  {item.label}
                </span>
                <ChevronRight size={14} className={`chev ${subAberto === item.label ? 'chev-aberto-h' : ''}`} />
              </button>
              {subAberto === item.label && posSub && (
                <div
                  className="dropdown-painel"
                  style={{
                    position: 'fixed',
                    top: posSub.top,
                    left: posSub.left,
                    margin: 0,
                  }}
                >
                  {item.children.map((c) => (
                    <button
                      key={c.to ?? c.label}
                      type="button"
                      onClick={() => c.to && navegar(c.to)}
                      disabled={bloqueado(c)}
                      title={bloqueado(c) ? DICA_BLOQUEADO : undefined}
                      style={bloqueado(c) ? estiloBloqueado : undefined}
                      className={`dropdown-item ${isAtivo(c.to) ? 'dropdown-item-ativo' : ''}`}
                    >
                      <c.icon size={14} />
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button
              key={item.to ?? item.label}
              type="button"
              onClick={() => executarItem(item)}
              disabled={bloqueado(item)}
              title={bloqueado(item) ? DICA_BLOQUEADO : undefined}
              style={bloqueado(item) ? estiloBloqueado : undefined}
              className={`dropdown-item ${isAtivo(item.to) ? 'dropdown-item-ativo' : ''}`}
            >
              <item.icon size={16} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
